// Calls (/staff/calls): who to call today by founder, with everything needed on one sheet. Attio stays the record for its own
// notes and those links stay. A call logged here is a touch on the company record, written through POST /api/staff/crm/touches.
// Nothing here dials or sends: `tel:` and `mailto:` hand off to the founder's own phone and mail.
import { safeHref } from '../records/lib'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useSearchParams } from 'react-router-dom'
import { api } from '../../../lib'
import { Busy, Button, Err, Fresh, Page, Section, Sources, Strip, ago, errorText, useKit, when } from '../kit'
import type { CallEntry, Meeting, Note, Source } from '../../growth/types'
import { clock, current, isToday, plural, short } from '../../growth/format'

type Entry = CallEntry & { company_id: string | null; person_id: string | null; touches: { at: string; by: string | null; text: string }[] }
type Chase = { key: string; title: string; sub?: string; started?: string; since?: string; to: string }
type Calls = {
  at: string; today: string; founder: string | null; date: string | null; draft: boolean; by_founder: Record<string, number>; entries: Entry[]; list_read: boolean
  meetings: (Omit<Meeting, 'company'> & { name: string; company: string | null; company_id: string | null; person_id: string | null })[]; next_booked: string | null; chase: Chase[]; sources: (Omit<Source, 'state'> & { state: Source['state'] })[]
}

const KEY = ['staff', 'calls']
const ATTIO_HOME = 'https://app.attio.com'
const FOUNDERS = ['Connor', 'Nikhil', 'Malcolm']
const unreachable = (e: Entry) => !e.phone && !e.linkedin && !e.email
const loggedToday = (e: Entry) => e.logged.some((l) => isToday(l.at)) || e.touches.some((t) => isToday(t.at))
const record = (id: string | null, domain: string) => (id ? `/staff/companies/${id}` : `/staff/growth/prospect/${encodeURIComponent(domain)}`)

export function CallsPage() {
  const q = useQuery({ queryKey: KEY, queryFn: () => api<Calls>('GET', '/api/staff/calls'), refetchInterval: 300_000 })
  return (
    <Page title="Calls" testid="growth-calls">
      {q.isLoading ? <Busy what="Reading the call list" /> : q.error ? <Err error={q.error} /> : q.data ? <Body d={q.data} /> : null}
    </Page>
  )
}

function Body({ d }: { d: Calls }) {
  const entries = d.entries
  const [who, setWho] = useState<string>(d.founder && entries.some((e) => e.founder === d.founder) ? d.founder : 'All')
  const shown = useMemo(() => (who === 'All' ? entries : entries.filter((e) => e.founder === who)), [entries, who])
  const [params] = useSearchParams()
  const [picked, setPicked] = useState<string | null>(null)
  const called = params.get('call')
  // The rail opens on the next booked call. A link can name a booking or a person on the list instead.
  const [booked, setBooked] = useState<string | null>(params.get('booked') ?? (called ? null : d.next_booked))
  const id = (e: Entry) => `${e.name}|${e.domain}|${e.founder}`
  const cur = shown.find((e) => id(e) === picked) ?? shown.find((e) => e.name === called) ?? shown.find((e) => !unreachable(e)) ?? shown[0] ?? null
  const sheet = useRef<HTMLDivElement>(null)
  const meeting = d.meetings.find((m) => m.start === booked) ?? null
  const pick = (e: Entry) => {
    setBooked(null)
    setPicked(id(e))
    if (window.matchMedia('(max-width: 860px)').matches) sheet.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }
  useEffect(() => { setPicked(null) }, [who])
  const crm = d.sources.find((s) => s.id === 'crm')
  const attioRead = crm?.state === 'ok'
  const listRead = d.sources.find((s) => s.id === 'call_list')
  const isCurrent = !!d.date && d.date === d.today
  const done = entries.filter(loggedToday).length
  const title = !d.list_read ? 'No call list yet' : isCurrent ? `${plural(entries.length, 'call')} to make today` : 'No call list for today'
  const sub = !d.list_read ? 'It is built on weekday mornings'
    : !isCurrent ? `The last list is from ${short(d.date)}, shown below`
    : `${done === 0 ? 'None' : `${done} of ${entries.length}`} logged today. The goal is 10 to 15 a day${d.draft ? '. This list is a draft' : ''}`
  return (
    <>
      <div className="st-cl-head">
        <p><span className="st-strong">{title}</span> <span className="st-muted">{sub}</span></p>
        <Fresh at={listRead?.as_of} label="Call list" limit={current(listRead) ? undefined : 0} testid="calls-read" />
        <details className="st-td-details" data-testid="calls-sources">
          <summary>Sources</summary>
          <Sources>
            {['call_list', 'calendly', 'crm'].map((sid) => {
              const s = d.sources.find((x) => x.id === sid)
              return <Fresh key={sid} at={s?.as_of} label={s?.label ?? { call_list: 'Call list', calendly: 'Bookings', crm: 'Attio' }[sid]} limit={s && current(s) ? undefined : 0} testid={`calls-fresh-${sid}`} />
            })}
          </Sources>
        </details>
      </div>
      {entries.length ? (
        <Strip testid="calls-founder" value={who} onChange={setWho} items={[{ key: 'All', label: 'All', n: entries.length }, ...FOUNDERS.map((f) => ({ key: f, label: f, n: d.by_founder[f] ?? 0 }))]} />
      ) : null}
      <div className="st-cl-split">
        <div className="st-cl-queue">
          <Booked rows={d.meetings} on={booked} pick={setBooked} />
          {shown.length ? (
            <Section title="Call" count={shown.length}>
              {shown.map((e) => (
                <button key={id(e)} type="button" className={`st-cl-row${!meeting && cur && id(cur) === id(e) ? ' on' : ''}`} aria-pressed={!meeting && !!cur && id(cur) === id(e)} onClick={() => pick(e)} data-testid="growth-call-row">
                  <span>{e.name}</span>
                  {unreachable(e) ? <span className="st-bad">No way to reach</span> : <span className="st-muted">{e.company}</span>}
                </button>
              ))}
            </Section>
          ) : null}
          <Chases rows={d.chase} />
        </div>
        <div className="st-cl-sheet" ref={sheet}>
          {meeting ? <BookedSheet key={meeting.start} m={meeting} /> : cur ? <Sheet key={id(cur)} e={cur} attioRead={attioRead} attioAge={crm?.as_of} /> : <p className="st-note">{listRead ? 'The queue is empty for you today' : 'The call list was not read'}</p>}
        </div>
      </div>
    </>
  )
}

function Booked({ rows, on, pick }: { rows: Calls['meetings']; on: string | null; pick: (start: string) => void }) {
  if (rows.length === 0) return null
  return (
    <Section title="Booked" count={rows.length} testid="growth-booked">
      {rows.slice(0, 5).map((m) => (
        <button key={m.start} type="button" className={`st-cl-row${m.start === on ? ' on' : ''}`} aria-pressed={m.start === on} onClick={() => pick(m.start)} data-testid="calls-booked-row">
          <span>{m.name}{m.company ? <span className="st-muted">, {m.company}</span> : null}</span><span className="st-muted">{isToday(m.start) ? `Today, ${clock(m.start)}` : when(m.start)}</span>
        </button>
      ))}
    </Section>
  )
}

/** A booked call on its own sheet: when, who, how to reach them, the record, and Attio. */
function BookedSheet({ m }: { m: Calls['meetings'][number] }) {
  const record = m.company_id ? `/staff/companies/${m.company_id}` : m.person_id ? `/staff/people/${m.person_id}` : null
  return (
    <div className="st-stack" data-testid="calls-booked-sheet">
      <div className="st-cl-sheet-head">
        <div>
          <h2 className="st-cl-name">{m.name}</h2>
          <p className="st-note">{record ? <Link className="st-link" to={record} data-testid="calls-booked-record">{m.company ?? 'Open the record'}</Link> : m.company ?? 'No record yet'}</p>
        </div>
        <a className="st-btn" href={m.attio_url ?? ATTIO_HOME} target="_blank" rel="noreferrer" data-testid="calls-booked-attio">{m.attio_url ? 'Open in Attio' : 'Find in Attio'}</a>
      </div>
      <dl className="st-facts">
        <dt>When</dt><dd>{isToday(m.start) ? `Today, ${clock(m.start)}` : when(m.start)}</dd>
        <dt>Email</dt><dd>{m.email ? <a className="st-link" href={`mailto:${m.email}`} data-testid="calls-booked-mailto">{m.email}</a> : <span className="st-muted">None on file</span>}</dd>
      </dl>
    </div>
  )
}

function Chases({ rows }: { rows: Chase[] }) {
  if (rows.length === 0) return null
  return (
    <Section title="Chase, card request not sent" count={rows.length} testid="growth-chase">
      {rows.slice(0, 8).map((r) => (
        <Link key={r.key} className="st-cl-row" to={r.to}><span>{r.title}</span><span className="st-muted">Started {short(r.started)}</span></Link>
      ))}
    </Section>
  )
}

function Sheet({ e, attioRead, attioAge }: { e: Entry; attioRead: boolean; attioAge?: string | null }) {
  const attio = e.attio_person ?? e.attio_company
  const handle = (e.linkedin ?? '').replace(/\/$/, '').split('/').pop()
  return (
    <div className="st-stack" data-testid="growth-sheet">
      <div className="st-cl-sheet-head">
        <div>
          <h2 className="st-cl-name">{e.name}</h2>
          <p className="st-note">{e.person_id && !e.company_id ? <Link className="st-link" to={`/staff/people/${e.person_id}`}>{[e.title, e.company].filter(Boolean).join(', ') || 'Open the record'}</Link> : e.domain ? <Link className="st-link" to={record(e.company_id, e.domain)}>{[e.title, e.company].filter(Boolean).join(', ') || 'Open the record'}</Link> : [e.title, e.company].filter(Boolean).join(', ')}</p>
        </div>
        {safeHref(e.page) ? <a className="st-btn" href={safeHref(e.page)} target="_blank" rel="noreferrer" data-testid="calls-page-link">Open their page</a> : null}
      </div>
      <dl className="st-facts">
        <dt>{e.phone_kind === 'company' || /main/.test(e.phone_kind) ? 'Phone, main line' : 'Phone'}</dt><dd>{e.phone ? <a className="st-link" href={`tel:${e.phone}`} data-testid="calls-tel">{e.phone}</a> : <span className="st-muted">None on file</span>}</dd>
        <dt>Email</dt><dd>{e.email ? <a className="st-link" href={`mailto:${e.email}`} data-testid="calls-mailto">{e.email}</a> : <span className="st-muted">None on file</span>}</dd>
        <dt>LinkedIn</dt><dd>{safeHref(e.linkedin) ? <a className="st-link" href={safeHref(e.linkedin)} target="_blank" rel="noreferrer" data-testid="calls-linkedin">{handle}</a> : <span className="st-muted">None on file</span>}</dd>
      </dl>
      {e.say ? <div className="st-banner">{e.say}</div> : null}
      {e.why ? <p><span className="st-muted">Why now </span>{e.why}</p> : null}
      <LogCall e={e} />
      <div className="st-cl-log">
        <div>
          <h3 className="st-fn-step">Call log</h3>
          <Log e={e} rows={e.logged} attioRead={attioRead} attioAge={attioAge} />
        </div>
        <a className="st-btn" href={safeHref(attio) ?? ATTIO_HOME} target="_blank" rel="noreferrer" data-testid="growth-attio-link">{attio ? 'Open in Attio' : 'Find in Attio'}</a>
      </div>
    </div>
  )
}

function Log({ e, rows, attioRead, attioAge }: { e: Entry; rows: Note[]; attioRead: boolean; attioAge?: string | null }) {
  const here = e.touches.map((t) => ({ key: `t${t.at}`, text: t.text, meta: [t.by, when(t.at), 'logged here'].filter(Boolean).join(', ') }))
  const old = attioAge ? `Attio ${ago(attioAge)} old, so newer calls may be missing` : 'Attio not read yet'
  const there = rows.map((r, i) => ({ key: `a${i}`, text: r.title || r.text, meta: [r.by, when(r.at), 'from Attio'].filter(Boolean).join(', ') }))
  return (
    <>
      {[...here, ...there].map((r) => <div key={r.key} className="st-cl-note"><p>{r.text}</p><span className="st-note">{r.meta}</span></div>)}
      {here.length === 0 && there.length === 0 ? <p className="st-note">{!attioRead ? old : e.attio_person ? 'No calls logged yet' : 'No Attio record for this person yet'}</p> : null}
      {there.length === 0 && here.length > 0 && !attioRead ? <p className="st-note">{old}</p> : null}
    </>
  )
}

/** Writes one touch: channel call, direction out. A person with no company record yet gets one first, so the call is never
 * lost. Both writes are audited by their routes. */
function LogCall({ e }: { e: Entry }) {
  const qc = useQueryClient()
  const { toast } = useKit()
  const [open, setOpen] = useState(false)
  const [text, setText] = useState('')
  const save = useMutation({
    mutationFn: async () => {
      const company = e.company_id ?? (await api<{ id: string }>('POST', '/api/staff/crm/companies', { name: e.company || e.name, domain: e.domain || null })).id
      return api('POST', '/api/staff/crm/touches', { company_id: company, person_id: e.person_id, channel: 'call', direction: 'out', summary: `Call with ${e.name}: ${text.trim()}` })
    },
    onSuccess: () => { toast('Logged'); setText(''); setOpen(false); qc.invalidateQueries({ queryKey: KEY }) },
    onError: (err) => toast(errorText(err), { bad: true }),
  })
  if (!open) return <div><Button tone="primary" onClick={() => setOpen(true)} data-testid="calls-log">Log this call</Button></div>
  return (
    <div className="st-stack" data-testid="calls-log-form">
      <label className="st-field">What happened
        <textarea className="st-text" autoFocus value={text} maxLength={1900} onChange={(ev) => setText(ev.target.value)} data-testid="calls-log-text" />
      </label>
      <div className="st-row">
        <Button tone="primary" disabled={!text.trim() || save.isPending} onClick={() => save.mutate()} data-testid="calls-log-save">Save</Button>
        <Button tone="quiet" onClick={() => setOpen(false)}>Cancel</Button>
        <span className="st-note">{e.company_id ? `Adds a touch to ${e.company}` : `Adds ${e.company || e.name} as a company, then the touch`}</span>
      </div>
    </div>
  )
}
